import { useState } from 'react';

import { cn } from '@/utils/tailwind';

import { KeyValueInputProps } from '../types';
import { useJSONEditorStore } from '../useJSONEditorStore';
import { INPUT_KEY_CLASS_NAME, INPUT_VALUE_CLASS_NAME } from '../utils';
import ResizingInput from './ResizingInput';

export const KeyValueInput = ({
  keyInputRef,
  valueInputRef,
  isNewInput = false,
  keyName = '',
  value = '',
  isLast = false,
  nextKeyInputRef,
  index = -1,
  setPendingKey,
}: KeyValueInputProps) => {
  // Local state
  const [localKeyName, setLocalKeyName] = useState(keyName);
  const [pendingValue, setPendingValue] = useState(value);

  // Store
  const readOnly = useJSONEditorStore((state) => state.readOnly);
  const keyPlaceholder = useJSONEditorStore((state) => state.keyPlaceholder);
  const valuePlaceholder = useJSONEditorStore(
    (state) => state.valuePlaceholder
  );

  const updateKey = useJSONEditorStore((state) => state.updateKey);
  const updateValue = useJSONEditorStore((state) => state.updateValue);
  const handleKeyInputKeyDown = useJSONEditorStore(
    (state) => state.handleKeyInputKeyDown
  );
  const handleValueInputKeyDown = useJSONEditorStore(
    (state) => state.handleValueInputKeyDown
  );
  const handleAddKeyValue = useJSONEditorStore(
    (state) => state.handleAddKeyValue
  );

  return (
    <div className="flex w-full items-start">
      <div className="flex items-center">
        <ResizingInput
          ref={keyInputRef}
          value={localKeyName}
          placeholder={keyPlaceholder}
          onChange={(newKey) => {
            setLocalKeyName(newKey);
            if (isNewInput && setPendingKey) {
              setPendingKey(newKey);
            }
          }}
          onBlur={(newKey) => {
            if (isNewInput) {
              if (newKey.trim() !== '') {
                handleAddKeyValue(newKey, pendingValue || '');
              }
            } else {
              updateKey(keyName, newKey);
            }
          }}
          onKeyDown={(e) => {
            handleKeyInputKeyDown(e, {
              isNewInput,
              localKeyName,
              value,
              keyName,
              index,
              isLast,
              valueInputRef,
            });
          }}
          readOnly={!isNewInput && readOnly}
          className={cn(
            'max-w-[150px] truncate font-medium text-secondary',
            index === 0 && INPUT_KEY_CLASS_NAME
          )}
        />
      </div>
      <span className="mr-4"></span>
      <div className="w-full min-w-0 flex-1">
        <ResizingInput
          ref={valueInputRef}
          value={isNewInput ? pendingValue : value}
          placeholder={valuePlaceholder}
          onChange={(newValue) => {
            if (isNewInput) {
              setPendingValue(newValue);
            } else if (!readOnly) {
              updateValue(keyName, newValue);
            }
          }}
          onBlur={(newValue) => {
            if (isNewInput) {
              setPendingValue(newValue);
            } else {
              updateValue(keyName, newValue);
            }
          }}
          onKeyDown={(e) => {
            handleValueInputKeyDown(e, {
              isNewInput,
              localKeyName,
              pendingValue,
              keyName,
              isLast,
              nextKeyInputRef,
              setLocalKeyName,
              setPendingValue,
              setPendingKey,
              keyInputRef,
            });
          }}
          readOnly={!isNewInput && readOnly}
          className={cn(
            'w-full text-tertiary',
            index === 0 && INPUT_VALUE_CLASS_NAME
          )}
          multiline={true}
        />
      </div>
    </div>
  );
};
